import { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useIdentity } from './useIdentity';
import { getPushPermissionState, maintainPushRegistration, invalidatePushMaintenance } from '../utils/push';

/**
 * Silent push-registration maintenance at authenticated startup (Phase 11
 * correction). For an identity that already opted in (push_enabled === true)
 * on a device where permission is ALREADY granted, idempotently refresh this
 * device's token registration — token rotation would otherwise silently break
 * delivery for opted-in users.
 *
 * This hook can never prompt: it only reads permission state, and
 * maintainPushRegistration() is structurally unable to request permission.
 * default / denied / unsupported → nothing happens. push_enabled false →
 * not even the permission read runs. Failures are swallowed (startup is
 * never blocked, the preference is never changed). Deduplication across
 * StrictMode/remounts/Settings lives in the module-level coordinator in
 * utils/push, keyed by the AuthContext identityKey — not in effect timing.
 */
export function usePushStartupMaintenance(): void {
  const { isLoggedIn, identityKey } = useAuth();
  const identity = useIdentity();
  const pushEnabled = identity?.pushEnabled === true;

  // Logout / account switch: drop maintenance state so an in-flight run
  // aborts before its backend write and the next identity starts fresh.
  useEffect(() => {
    if (!isLoggedIn) invalidatePushMaintenance();
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn || !identityKey || !pushEnabled) return;
    let alive = true;
    void getPushPermissionState().then(state => {
      if (!alive || state !== 'granted') return;
      void maintainPushRegistration(identityKey).catch(() => {});
    });
    return () => { alive = false; };
  }, [isLoggedIn, identityKey, pushEnabled]);
}
